import React, { useMemo, useState } from "react";
import Navbar from "../Navbar/Navbar";
import Bredcom from "../Bredcom/Bredcom";
import Footer from "../Footer/Footer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

function PrelazSaDrugihFakulteta() {
  const [t] = useTranslation();

  // Centralized links (keep the existing PDFs)
  const DOCS = useMemo(
    () => ({
      regulation: {
        href: "/Prilozi/Pravilnik o prepisu studenata.pdf",
        title: t("prelazak.doc.regulationTitle"),
        text: t("prelazak.doc.regulationText"),
        icon: "fas fa-balance-scale",
      },
      request: {
        href: "/Prilozi/ZAHTJEV ZA PREPIS.pdf",
        title: t("prelazak.doc.requestTitle"),
        text: t("prelazak.doc.requestText"),
        icon: "fas fa-file-signature",
      },
    }),
    [t],
  );

  const email = t("prelazak.contactEmail");
  const subject = t("prelazak.mailSubject");
  const body = t("prelazak.mailBody");

  const mailtoHref = useMemo(() => {
    const s = encodeURIComponent(subject);
    const b = encodeURIComponent(body);
    return `mailto:${email}?subject=${s}&body=${b}`;
  }, [email, subject, body]);

  const [copied, setCopied] = useState(false);

  const onCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch (e) {
      // Clipboard may be blocked (HTTP / permissions). Fallback: open mail client.
      window.location.href = mailtoHref;
    }
  };

  const articles = useMemo(
    () => Array.from({ length: 13 }, (_, i) => i + 1),
    [],
  );

  const DocCard = ({ doc }) => (
    <div className="transfer-docCard">
      <div className="transfer-docCard__icon" aria-hidden="true">
        <i className={doc.icon} />
      </div>

      <div className="transfer-docCard__body">
        <div className="transfer-docCard__title">{doc.title}</div>
        <div className="transfer-docCard__text">{doc.text}</div>

        <div className="transfer-docCard__actions">
          <a
            className="transfer-btn transfer-btn--primary"
            href={doc.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("prelazak.doc.open")} <i className="fas fa-external-link-alt" />
          </a>

          <a
            className="transfer-btn transfer-btn--ghost"
            href={doc.href}
            download
          >
            {t("prelazak.doc.download")} <i className="fas fa-download" />
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Navbar />
      <Bredcom
        title={t("breadcrumb.home")}
        subtitle={t("pages.prelazak-sa-drugih-fakulteta")}
      />

      <div className="transfer-page container mt-4 pt-40">
        <div className="row">
          {/* MAIN */}
          <div className="col-md-8">
            {/* HERO */}
            <section className="transfer-hero">
              <div className="transfer-hero__content">
                <div className="transfer-eyebrow">
                  <span className="transfer-pill">
                    <i className="fas fa-exchange-alt" aria-hidden="true" />{" "}
                    {t("admission.Prelazak")}
                  </span>
                </div>

                <h1 className="transfer-title">
                  {t("pages.prelazak-sa-drugih-fakulteta")}
                </h1>

                <p className="transfer-lead">{t("prelazak.heroLead")}</p>

                <div className="transfer-actions">
                  <a
                    className="transfer-btn transfer-btn--primary"
                    href="#transfer-docs"
                  >
                    {t("prelazak.quickDocs")} <i className="fas fa-file-pdf" />
                  </a>

                  <a
                    className="transfer-btn transfer-btn--ghost"
                    href="#transfer-recognition"
                  >
                    {t("prelazak.quickRecognition")}{" "}
                    <i className="fas fa-list-ul" />
                  </a>

                  <a
                    className="transfer-btn transfer-btn--ghost"
                    href="#transfer-send"
                  >
                    {t("prelazak.quickEmail")}{" "}
                    <i className="fas fa-paper-plane" />
                  </a>
                </div>

                <div className="transfer-meta">
                  <span className="transfer-badge">
                    <i className="fas fa-file-lines" aria-hidden="true" />{" "}
                    {t("prelazak.metaPdf")}
                  </span>
                  <span className="transfer-badge">
                    <i className="fas fa-shield-alt" aria-hidden="true" />{" "}
                    {t("prelazak.metaOfficial")}
                  </span>
                </div>
              </div>

              <div className="transfer-hero__aside">
                <div className="transfer-asideCard">
                  <div className="transfer-asideCard__title">
                    {t("prelazak.asideTitle")}
                  </div>
                  <div className="transfer-asideCard__text">
                    {t("prelazak.asideText")}
                  </div>

                  <div className="transfer-asideCard__actions">
                    <Link
                      className="transfer-btn transfer-btn--primary"
                      to="/svi-studijski-programi"
                    >
                      {t("prelazak.ctaPrograms")}{" "}
                      <i className="fas fa-graduation-cap" />
                    </Link>
                    <Link
                      className="transfer-btn transfer-btn--ghost"
                      to="/cijene-studija"
                    >
                      {t("prelazak.ctaTuition")}{" "}
                      <i className="fas fa-euro-sign" />
                    </Link>
                    <Link
                      className="transfer-btn transfer-btn--ghost"
                      to="/kontakt"
                    >
                      {t("prelazak.ctaContact")} <i className="fas fa-phone" />
                    </Link>
                  </div>
                </div>
              </div>
            </section>

            {/* DOCUMENTS */}
            <section id="transfer-docs" className="transfer-card">
              <div className="transfer-card__head">
                <h2 className="transfer-h2">{t("prelazak.docsTitle")}</h2>
                <p className="transfer-muted">{t("prelazak.docsLead")}</p>
              </div>

              <div className="transfer-docGrid">
                <DocCard doc={DOCS.regulation} />
                <DocCard doc={DOCS.request} />
              </div>
            </section>

            {/* SEND */}
            <section id="transfer-send" className="transfer-card">
              <div className="transfer-card__head">
                <h2 className="transfer-h2">{t("prelazak.howTitle")}</h2>
                <p className="transfer-muted">{t("prelazak.howLead")}</p>
              </div>

              <ol className="transfer-steps">
                <li className="transfer-step">
                  <div className="transfer-step__icon" aria-hidden="true">
                    <i className="fas fa-download" />
                  </div>
                  <div className="transfer-step__body">
                    <div className="transfer-step__title">
                      {t("prelazak.howStep1Title")}
                    </div>
                    <div className="transfer-step__text">
                      {t("prelazak.howStep1Text")}
                    </div>
                  </div>
                </li>

                <li className="transfer-step">
                  <div className="transfer-step__icon" aria-hidden="true">
                    <i className="fas fa-folder-open" />
                  </div>
                  <div className="transfer-step__body">
                    <div className="transfer-step__title">
                      {t("prelazak.howStep2Title")}
                    </div>
                    <div className="transfer-step__text">
                      {t("prelazak.howStep2Text")}
                    </div>
                  </div>
                </li>

                <li className="transfer-step">
                  <div className="transfer-step__icon" aria-hidden="true">
                    <i className="fas fa-paper-plane" />
                  </div>
                  <div className="transfer-step__body">
                    <div className="transfer-step__title">
                      {t("prelazak.howStep3Title")}
                    </div>
                    <div className="transfer-step__text">
                      {t("prelazak.howStep3Text")}
                    </div>
                  </div>
                </li>

                <li className="transfer-step">
                  <div className="transfer-step__icon" aria-hidden="true">
                    <i className="fas fa-clipboard-check" />
                  </div>
                  <div className="transfer-step__body">
                    <div className="transfer-step__title">
                      {t("prelazak.howStep4Title")}
                    </div>
                    <div className="transfer-step__text">
                      {t("prelazak.howStep4Text")}
                    </div>
                  </div>
                </li>
              </ol>

              <div className="transfer-callout">
                <div className="transfer-callout__icon" aria-hidden="true">
                  <i className="fas fa-envelope" />
                </div>

                <div className="transfer-callout__body">
                  <div className="transfer-callout__title">
                    {t("prelazak.emailBoxTitle")}
                  </div>

                  <div className="transfer-callout__text">
                    {t("prelazak.emailBoxText")}{" "}
                    <a className="transfer-link" href={`mailto:${email}`}>
                      {email}
                    </a>
                  </div>

                  <div className="transfer-callout__actions">
                    <a
                      className="transfer-btn transfer-btn--primary"
                      href={mailtoHref}
                    >
                      {t("prelazak.emailCta")}{" "}
                      <i className="fas fa-paper-plane" />
                    </a>

                    <button
                      type="button"
                      className="transfer-btn transfer-btn--ghost"
                      onClick={onCopyEmail}
                    >
                      {copied ? t("prelazak.copied") : t("prelazak.copyEmail")}{" "}
                      <i className="fas fa-copy" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="transfer-note">
                <i className="fas fa-info-circle" aria-hidden="true" />
                <span>{t("prelazak.emailNote")}</span>
              </div>
            </section>

            {/* RECOGNITION DOCUMENTS */}
            <section id="transfer-recognition" className="transfer-card">
              <div className="transfer-card__head">
                <h2 className="transfer-h2">{t("prelazak.p14")}</h2>
                <p className="transfer-muted">
                  {t("prelazak.recognitionLead")}
                </p>
              </div>

              <div className="transfer-twoCols">
                <div className="transfer-subCard">
                  <h3 className="transfer-h3">{t("prelazak.p15")}</h3>
                  <ul className="transfer-list">
                    <li>{t("prelazak.l1")}</li>
                    <li>{t("prelazak.l2")}</li>
                    <li>{t("prelazak.l3")}</li>
                    <li>{t("prelazak.l4")}</li>
                  </ul>
                </div>

                <div className="transfer-subCard">
                  <h3 className="transfer-h3">{t("prelazak.p16")}</h3>
                  <ul className="transfer-list">
                    <li>{t("prelazak.l5")}</li>
                    <li>{t("prelazak.l6")}</li>
                    <li>{t("prelazak.l7")}</li>
                    <li>{t("prelazak.l8")}</li>
                    <li>{t("prelazak.l9")}</li>
                    <li>{t("prelazak.l10")}</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* KEY POINTS (plain-language) */}
            <section className="transfer-card">
              <div className="transfer-card__head">
                <h2 className="transfer-h2">{t("prelazak.keyPointsTitle")}</h2>
                <p className="transfer-muted">{t("prelazak.keyPointsLead")}</p>
              </div>

              <ul className="transfer-kp">
                <li>
                  <i className="fas fa-check" aria-hidden="true" />{" "}
                  {t("prelazak.key1")}
                </li>
                <li>
                  <i className="fas fa-check" aria-hidden="true" />{" "}
                  {t("prelazak.key2")}
                </li>
                <li>
                  <i className="fas fa-check" aria-hidden="true" />{" "}
                  {t("prelazak.key3")}
                </li>
                <li>
                  <i className="fas fa-check" aria-hidden="true" />{" "}
                  {t("prelazak.key4")}
                </li>
                <li>
                  <i className="fas fa-check" aria-hidden="true" />{" "}
                  {t("prelazak.key5")}
                </li>
              </ul>

              <details className="transfer-details">
                <summary className="transfer-details__summary">
                  <span>{t("prelazak.rulesToggle")}</span>
                  <i
                    className="fas fa-chevron-down transfer-details__chev"
                    aria-hidden="true"
                  />
                </summary>

                <div className="transfer-details__body">
                  <div className="transfer-articles">
                    {articles.map((n) => (
                      <div key={n} className="transfer-article">
                        <div className="transfer-article__head">
                          <span className="transfer-article__badge">
                            {t("prelazak.clan")} {n}
                          </span>
                        </div>
                        <p className="transfer-article__text">
                          {t(`prelazak.p${n}`)}
                        </p>
                      </div>
                    ))}

                    <div className="transfer-signature">
                      <strong>
                        {t("prelazak.nnv")}
                        <br />
                        {t("prelazak.predsjednikdekan")}
                        <br />
                        Prof. dr Božo Marđokić
                      </strong>
                    </div>
                  </div>
                </div>
              </details>
            </section>

            {/* FAQ */}
            <section id="transfer-faq" className="transfer-card">
              <div className="transfer-card__head">
                <h2 className="transfer-h2">{t("prelazak.faqTitle")}</h2>
                <p className="transfer-muted">{t("prelazak.faqLead")}</p>
              </div>

              <div className="transfer-faq">
                <details className="transfer-faq__item">
                  <summary className="transfer-faq__q">
                    {t("prelazak.faq.q1")}
                    <i
                      className="fas fa-chevron-down transfer-details__chev"
                      aria-hidden="true"
                    />
                  </summary>
                  <div className="transfer-faq__a">{t("prelazak.faq.a1")}</div>
                </details>

                <details className="transfer-faq__item">
                  <summary className="transfer-faq__q">
                    {t("prelazak.faq.q2")}
                    <i
                      className="fas fa-chevron-down transfer-details__chev"
                      aria-hidden="true"
                    />
                  </summary>
                  <div className="transfer-faq__a">{t("prelazak.faq.a2")}</div>
                </details>

                <details className="transfer-faq__item">
                  <summary className="transfer-faq__q">
                    {t("prelazak.faq.q3")}
                    <i
                      className="fas fa-chevron-down transfer-details__chev"
                      aria-hidden="true"
                    />
                  </summary>
                  <div className="transfer-faq__a">{t("prelazak.faq.a3")}</div>
                </details>

                <details className="transfer-faq__item">
                  <summary className="transfer-faq__q">
                    {t("prelazak.faq.q4")}
                    <i
                      className="fas fa-chevron-down transfer-details__chev"
                      aria-hidden="true"
                    />
                  </summary>
                  <div className="transfer-faq__a">{t("prelazak.faq.a4")}</div>
                </details>
              </div>
            </section>
          </div>

          {/* SIDEBAR (keep existing widgets) */}
          <div className="col-md-4 pt-10">
            <aside className="sidebar-widget">
              <section
                id="custom_html-5"
                className="widget_text widget widget_custom_html"
              >
                <h2 className="widget-title">{t("sidebar.follow")}</h2>
                <div className="textwidget custom-html-widget">
                  <div className="widget-social">
                    <a
                      href="https://www.facebook.com/fpebar.me"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Facebook"
                    >
                      <i className="fab fa-facebook-f" />
                    </a>
                    <a
                      href="https://www.instagram.com/fpepbar/"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Instagram"
                    >
                      <i className="fab fa-instagram" />
                    </a>
                    <a
                      href="https://www.youtube.com/@fpepbar"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="YouTube"
                    >
                      <i className="fab fa-youtube" />
                    </a>
                    <a
                      href="https://www.tiktok.com/@fpepbar"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Follow us on Tiktok"
                      title="Tiktok"
                    >
                      <i className="fab fa-tiktok" />
                    </a>
                    <a
                      href="https://www.linkedin.com/school/fpepbar/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Follow us on LinkedIn"
                      title="LinkedIn"
                    >
                      <i className="fab fa-linkedin-in" />
                    </a>
                  </div>
                </div>
              </section>

              <section id="categories-1" className="widget widget_categories">
                <h2 className="widget-title">{t("sidebar.useful")}</h2>
                <ul>
                  <li className="cat-item cat-item-16">
                    <Link to="/o-fpep">{t("about.fpep")}</Link>
                  </li>
                  <li className="cat-item cat-item-23">
                    <Link to="/akreditacija-fpep">
                      {t("admission.Akreditacija")}
                    </Link>
                  </li>
                  <li className="cat-item cat-item-18">
                    <Link to="/upis">{t("upis.title")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/cijene-studija">{t("admission.Skolarina")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/erasmus">{t("saradnja.erasmus")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/projekti">{t("saradnja.projekti")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/dom-studenata">{t("studenti.dom")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/galerija">{t("navbar.Gallery")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/novosti">{t("navbar.News")}</Link>
                  </li>
                </ul>
              </section>

              <section
                id="recent-posts-4"
                className="widget widget_recent_entries"
              >
                <h2 className="widget-title">{t("sidebar.study")}</h2>
                <ul>
                  <li>
                    <span className="post-date">
                      <strong>{t("programs.osnovne")}</strong> <br />
                    </span>
                    <Link to="/racunovodstvo-finansije-i-bankarstvo">
                      {t("osnovne.rfb")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/menadzment">
                      {t("osnovne.menadzment")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/pravo-osnovne">
                      {t("osnovne.pravo")}
                      <br />
                    </Link>
                  </li>

                  <li>
                    <span className="post-date">
                      <strong>{t("programs.master")}</strong> <br />
                    </span>
                    <Link to="/poslovna-ekonomija">
                      {t("master.poslovnaEkonomija")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/ekonomija-javnog-sektora">
                      {t("master.ekonomijaJavnogSektora")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/pravo-master">
                      {t("master.pravo")}
                      <br />
                    </Link>
                  </li>

                  <li>
                    <span className="post-date">
                      <strong>{t("programs.specijalisticke")}</strong> <br />
                    </span>
                    <Link to="/menadzment-javnog-sektora-specijalisticke">
                      {t("specijalisticke.mjs")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/menadzment-profitnog-sektora-specijalisticke">
                      {t("specijalisticke.mps")}
                      <br />
                    </Link>
                  </li>
                </ul>
              </section>
            </aside>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default PrelazSaDrugihFakulteta;
